<script setup lang="ts">
import { api } from '@/api';
import { ProtocolDto } from '@/api/generator';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import OverviewTable from '@/components/OverviewTable/OverviewTable.vue';
import EntityChange from '@/components/EntityHistory/EntityChange.vue';
import { useLoading } from '@/utils/useLoading';
import { ref, useTemplateRef, watchEffect } from 'vue';
import { routenames } from '@/router/routenames';
import { buildOdataQuery } from '@/utils/odata';
import { alphabeticalStringCompare, localizedEnumCompare } from '@/utils/compareAndSort';
import type { SortItem } from '@/components/OverviewTable/types';
import { useTranslation } from 'i18next-vue';
import ContextMenu from '@/components/ContextMenu/ContextMenu.vue';
import { ContextMenuAction } from '@/components/ContextMenu/types';
import {
  globalContextMenuHeaderItemDefinition,
  getDefaultContextMenuActions,
} from '@/components/ContextMenu/utils';
import OverviewTableHeader from '@/components/OverviewTable/OverviewTableHeader.vue';
import { useRouter } from 'vue-router';
import PtwAddButton from '@/components/buttons/PtwAddButton.vue';
import ProtocolOverviewFilter from './components/ProtocolOverviewFilter.vue';
import { ProtocolOverviewFilterObject } from './types';
import { I18nKey } from '@/utils/typings';
import CommentCount from '@/components/forms/CommentCount/CommentCount.vue';
import { EntityInfo } from '@/components/confirmationModals/types';
import { getDefaultDeleteConfirmationOptions } from '@/components/confirmationModals/defaults';
import { useDeleteProtocolEntityWithConfirmation } from './utils';
import { TAB_PREFIX } from '@/components/confirmationModals/useFormConfirmationModal';
import { useProtocolsStore } from '@/stores/protocolsStore.ts';
import { getEntityChangeEvent } from '@/components/EntityHistory/entityChange';
import { useRemainingViewportHeight } from '@/utils/useRemainingVueport';
import { useTableScrollElement } from '@/utils/vuetifyMenuHelper';

const { t } = useTranslation();
const protocolStore = useProtocolsStore();
const router = useRouter();
const tableRef = useTemplateRef<HTMLDivElement>('table');
const { height } = useRemainingViewportHeight(tableRef);
const tableScrollElement = useTableScrollElement(tableRef);

const headers = [
  {
    title: ``,
    key: 'name',
  },
  {
    title: t('Description'),
    key: 'description',
  },
  {
    title: t('DateOfPublication'),
    key: 'dateOfPublication',
  },
  {
    title: t('StateOfUse'),
    key: 'state',
    sort: localizedEnumCompare('StateOfUseEnum'),
  },
  {
    title: t('Added'),
    key: 'creationTime',
  },
  {
    title: t('LastModification'),
    key: 'lastModificationTime',
  },
  globalContextMenuHeaderItemDefinition,
] as const;

const filterObject = ref<ProtocolOverviewFilterObject>({});

const customKeySort = {
  name: alphabeticalStringCompare,
};

const sortBy: SortItem[] = [{ key: 'name', order: 'asc' }];

const protocols = ref<ProtocolDto[]>();

const {
  action: loadProtocols,
  loading,
  loadingFirstTime,
} = useLoading(async () => {
  const { result } = await api.protocolsApi.getProtocolList(
    buildOdataQuery({
      search: filterObject.value?.search,
    }),
  );

  if (result?.value) {
    protocols.value = result.value;
  }
}, true);

function navigateTo(id: string, hash?: string) {
  router.push({
    name: routenames.protocols.details,
    hash,
    params: { id },
  });
}

const { action: deleteAction } = useDeleteProtocolEntityWithConfirmation();

function getContextMenuActions(item: ProtocolDto): ContextMenuAction[] {
  const defaultContextMenuActions = getDefaultContextMenuActions(item);
  return [
    {
      ...defaultContextMenuActions.view,
      to: {
        name: routenames.protocols.details,
        params: { id: item.id },
      },
    },
    {
      ...defaultContextMenuActions.edit,
      to: {
        name: routenames.protocols.edit,
        params: { id: item.id },
      },
    },
    {
      ...defaultContextMenuActions.delete,
      callback: async () => {
        const { result } = await deleteAction({
          param: item.id,
          confirmationOptions: getDefaultDeleteConfirmationOptions(
            {
              ...(item as EntityInfo),
              typeName: 'Protocol_one',
            },
            t,
          ),
        });
        if (result?.success) {
          await protocolStore.update();
          loadProtocols();
        }
      },
    },
  ];
}

watchEffect(loadProtocols);
</script>

<template>
  <BasePage :loading="loadingFirstTime">
    <template #title>
      <BasePageTitle :title="$t('Protocol_other')" />
    </template>

    <template #actions>
      <PtwAddButton :to="{ name: routenames.protocols.add }"></PtwAddButton>
    </template>

    <ProtocolOverviewFilter v-model="filterObject" />
    <OverviewTable
      ref="table"
      class="mt-2"
      :headers="headers"
      :items="protocols"
      :loading="loading"
      itemLabel="Protocol"
      data-testid="DataTable_Protocol"
      :customKeySort="customKeySort"
      :sortBy="sortBy"
      :height="height"
      fixedHeader
      @click:row="
        (_event, data) => {
          navigateTo(data.item.id);
        }
      "
    >
      <template #header.name="headerProps">
        <OverviewTableHeader v-bind="headerProps">
          {{ $t('Name') }}
          <span class="font-weight-medium">| {{ $t('ID') }}</span>
        </OverviewTableHeader>
      </template>

      <template #item.name="{ item }">
        <IdentifierLabel :identifier="item.iuid" :label="item.name" />
        <CommentCount :value="item" @click.stop="navigateTo(item.id, `#${TAB_PREFIX}comments`)" />
      </template>

      <template #item.state="{ item }">
        {{ $t(`StateOfUseEnum.${item.state}` as I18nKey) }}
        <EntityChange
          :entityChange="getEntityChangeEvent(item, 'editing')"
          :showEmptyFieldSign="false"
        />
      </template>

      <template #item.creationTime="{ item }">
        <EntityChange :entityChange="getEntityChangeEvent(item, 'created')" />
      </template>

      <template #item.lastModificationTime="{ item }">
        <EntityChange :entityChange="getEntityChangeEvent(item, 'modified')" />
      </template>

      <template #item.contextMenu="{ item }">
        <ContextMenu
          :actions="getContextMenuActions(item)"
          :closeOnScrollElements="tableScrollElement"
        />
      </template>
    </OverviewTable>
  </BasePage>
</template>
